import type React from "react";
import { type ReactNode, createContext, useContext, useMemo, useRef, useState } from "react";
import type { ImperativePanelGroupHandle } from "react-resizable-panels";

interface PanelContextType {
  panelSizes: [number, number];
  updatePanelSizes: (leftSize: number, rightSize: number) => void;
  resetToDefaults: () => void;
  panelGroupRef: React.RefObject<ImperativePanelGroupHandle | null>;
  isAutoResizing: React.RefObject<boolean>;
  resetIsAutoResizing: () => void;
  startAutoResizing: () => void;
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (value: boolean) => void;
  setIsLeftPanelMaxed: (value: boolean) => void;
  isLeftPanelMaxed: boolean;
  totalPanelWidth: number;
  setTotalPanelWidth: (value: number) => void;
}

const PanelContext = createContext<PanelContextType | undefined>(undefined);

interface PanelProviderProps {
  children: ReactNode;
}

const DEFAULT_LEFT_SIZE = 30;
const DEFAULT_RIGHT_SIZE = 70;

export const PanelProvider: React.FC<PanelProviderProps> = ({ children }) => {
  const [panelSizes, setPanelSizes] = useState<[number, number]>([DEFAULT_LEFT_SIZE, DEFAULT_RIGHT_SIZE]);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isLeftPanelMaxed, setIsLeftPanelMaxed] = useState(false);
  const panelGroupRef = useRef<ImperativePanelGroupHandle>(null);
  const isAutoResizing = useRef(false);
  const [totalPanelWidth, setTotalPanelWidth] = useState(0);

  const updatePanelSizes = (leftSize: number, rightSize: number) => {
    setPanelSizes([leftSize, rightSize]);
    panelGroupRef.current?.setLayout([leftSize, rightSize]);

    try {
      localStorage.setItem("leftPanelSize", leftSize.toString());
      localStorage.setItem("rightPanelSize", rightSize.toString());
    } catch {
      // Silently handle localStorage errors
    }
  };

  const resetToDefaults = () => {
    setPanelSizes([DEFAULT_LEFT_SIZE, DEFAULT_RIGHT_SIZE]);
    try {
      localStorage.removeItem("leftPanelSize");
      localStorage.removeItem("rightPanelSize");
    } catch {
      // Silently handle localStorage errors
    }
  };

  const startAutoResizing = () => {
    isAutoResizing.current = true;
  };

  const resetIsAutoResizing = () => {
    isAutoResizing.current = false;
  };

  const value = useMemo<PanelContextType>(
    () => ({
      panelSizes,
      isAutoResizing,
      panelGroupRef,
      updatePanelSizes,
      resetToDefaults,
      resetIsAutoResizing,
      startAutoResizing,
      isSidebarCollapsed,
      setIsSidebarCollapsed,
      setIsLeftPanelMaxed,
      isLeftPanelMaxed,
      totalPanelWidth,
      setTotalPanelWidth,
    }),
    [panelSizes, isSidebarCollapsed, isLeftPanelMaxed, totalPanelWidth],
  );

  return <PanelContext.Provider value={value}>{children}</PanelContext.Provider>;
};

export const usePanelContext = (): PanelContextType => {
  const context = useContext(PanelContext);
  if (context === undefined) {
    throw new Error("usePanelContext must be used within a PanelProvider");
  }
  return context;
};
